import { useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

import { useManager } from './manager/useManager.js';
import ManagerForm from './manager/ManagerForm.jsx';
import ManagerView from './manager/ManagerView.jsx';
import './ManagerPage.css';

export default function ManagerPage() {
	const { user } = useParams();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const manager = useManager(user);

	/* /manager?id=123 — lets the home page hand an FPL ID straight over, and
	 * makes a team linkable before its owner has saved a username. */
	const idParam = searchParams.get('id');
	useEffect(() => {
		if (!user && idParam) manager.load({ managerId: idParam });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [idParam, user]);
	const { data, picks, status, error, errorReason, navigating, showPoints } = manager;

	const handleSave = async ({ managerId, username }) => {
		const result = await manager.saveUsername({ managerId, username });
		if (result?.username) navigate(`/manager/${result.username}`);
	};

	const shareUrl =
		status === 'ready' && data?.username
			? `${window.location.origin}/manager/${data.username}`
			: null;

	/* /api/manager has no `season` field; only the archive payload sets one. */
	const subtitle = [data?.season, data?.gw ? `Gameweek ${data.gw}` : null]
		.filter(Boolean)
		.join(' · ');

	return (
		<div className="manager-page">
			<header className="manager-page__intro">
				<h1>FPL season review</h1>
				<p className="manager-page__lede">
					Enter an FPL ID to see a team&apos;s squad, season stats and gameweek
					history.
				</p>
			</header>

			<ManagerForm
				initialManagerId={data?.manager_id ?? ''}
				initialUsername={user ?? data?.username ?? ''}
				onLoad={manager.load}
				onSave={handleSave}
				loading={status === 'loading'}
				saveState={manager.saveState}
				shareUrl={shareUrl}
			/>

			{/* A state like the season not having started is not a failed
			    lookup — no FPL ID would work — so it reads as information
			    rather than an error against the team just entered. */}
			{status === 'error' &&
				(errorReason === 'preseason' ? (
					<div className="notice notice--info" role="status">
						<span>{error}</span>
					</div>
				) : (
					<div className="notice notice--error" role="alert">
						<strong>Could not load that team.</strong>
						<span>{error}</span>
					</div>
				))}

			{status === 'loading' && (
				<div className="manager-page__loading" role="status">
					<span className="spinner" aria-hidden="true" />
					<span>Loading team…</span>
				</div>
			)}

			{status === 'ready' && (
				<ManagerView
					data={data}
					picks={picks}
					chips={data.chips}
					title={data.name}
					subtitle={subtitle}
					navigating={navigating}
					showPoints={showPoints}
					onPrev={() => manager.goToGameweek('prev')}
					onNext={() => manager.goToGameweek('next')}
					canGoPrev={manager.canGoPrev}
					canGoNext={manager.canGoNext}
				/>
			)}
		</div>
	);
}
